import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Shield } from "lucide-react";

/**
 * Reusable OTP verification widget.
 * Props:
 *  - phone (string): phone entered by the user (parent state)
 *  - onVerified (fn): called with the phone-verification JWT once OTP validates
 *  - verified (bool): parent tracks whether phone is already verified
 */
export default function PhoneVerify({ phone, onVerified, verified }) {
  const [otp, setOtp] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!phone || phone.length < 7) return toast.error("Enter a valid phone number first");
    setBusy(true);
    try {
      const { data } = await api.post("/otp/send", { phone });
      setSent(true);
      if (data.dev_otp) {
        toast.info(`Test OTP: ${data.dev_otp}`, { duration: 10000 });
      } else if (data.channel === "whatsapp") {
        toast.success(`Code sent on WhatsApp to ${phone}`);
      } else {
        toast.success(`Code sent by SMS to ${phone}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (!otp || otp.length !== 6) return toast.error("Enter the 6-digit OTP");
    setBusy(true);
    try {
      const { data } = await api.post("/otp/verify", { phone, otp });
      onVerified(data.token);
      toast.success("Phone verified");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (verified) {
    return (
      <div className="flex items-center gap-2 text-sm text-primary" data-testid="phone-verified-badge">
        <CheckCircle2 size={16} /> Phone verified
      </div>
    );
  }

  return (
    <div className="border border-dashed border-secondary/40 rounded-xl p-4 bg-secondary/5" data-testid="phone-verify">
      <div className="flex items-center gap-2 mb-2 text-secondary text-sm">
        <Shield size={14}/> Verify your phone with OTP to continue
      </div>
      {!sent ? (
        <Button type="button" onClick={send} disabled={busy} className="btn-pill bg-secondary text-secondary-foreground hover:bg-secondary/90" data-testid="otp-send-btn">
          {busy ? "Sending…" : "Send OTP"}
        </Button>
      ) : (
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label className="text-xs">6-digit OTP</Label>
            <Input inputMode="numeric" maxLength={6} value={otp} onChange={(e)=>setOtp(e.target.value.replace(/\D/g,""))}
              placeholder="123456" className="mt-1 bg-transparent tracking-widest text-center" data-testid="otp-input"/>
          </div>
          <Button type="button" onClick={verify} disabled={busy} className="btn-pill bg-primary text-primary-foreground" data-testid="otp-verify-btn">
            {busy ? "Verifying…" : "Verify"}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={send} disabled={busy} data-testid="otp-resend-btn">
            Resend
          </Button>
        </div>
      )}
    </div>
  );
}
